import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { object, string } from 'yup';

import CTA from 'Src/components/buttons/CTA';
import Copy from 'Src/components/Copy';
import { Flex } from 'Src/components/layout/Flex';
import { FlipAndTipLocationLogo } from 'Src/feature/flip-and-tip/FlipAndTipLocationLogo';
import { getTypedFormComponents } from 'Src/feature/form/getTypedFormComponents';
import { NumericKeyboard } from 'Src/feature/form/NumericKeyboard';
import { useForm } from 'Src/feature/form/useForm';
import { Page } from 'Src/feature/page/Page';
import { PageMain } from 'Src/feature/page/PageMain';
import { usePayment } from 'Src/feature/payment/usePayment';
import { useGlobalPhoneCodes } from 'Src/feature/phone/useGlobalPhoneCodes';
import { usePrinter } from 'Src/feature/printer/usePrinter';
import { useToast } from 'Src/feature/toasts/useToast';
import useRoute from 'Src/routes/useRoute';
import { phoneNumber, unknownToNumberOrUndefined } from 'Src/utils/format';
import { log } from 'Src/utils/log';

import { FlipAndTipReceiptToast } from './FlipAndTipReceiptToast';
import { FlipAndTipReceiptToggle, ReceiptOptions } from './FlipAndTipReceiptToggle';

import styles from './FlipAndTipEndPage.module.scss';

const PHONE_INPUT_MAX_CHAR = 10;
interface FormModel {
  receipt?: ReceiptOptions;
  email?: string;
  smsRegion?: string,
  smsPhone?: number,
}

const sectionId = 'FlipAndTipEndPage';

const TEST_ID = {
  PAGE: sectionId,
};

const { Field, Provider: FormProvider } = getTypedFormComponents<FormModel>();

const FlipAndTipEndPage: FC = () => {
  const { t } = useTranslation();
  const { route } = useRoute();
  const checkId = route.params.active.checkId ? parseInt(route.params.active.checkId, 10) : undefined;
  const paymentId = route.params.active.paymentId;
  const paymentSendReceipt = usePayment.mutate.sendReceipt();
  const printerSend = usePrinter.mutate.send();
  const toast = useToast();

  const globalPhoneCodes = useGlobalPhoneCodes();

  const form = useForm<FormModel>({
    defaultValues: {
      receipt: undefined,
      email: undefined,
      smsRegion: globalPhoneCodes.defaultOption.value,
      smsPhone: undefined,
    },
    mode: 'onChange',
    schema: object().shape({
      receipt: string(),
      email: string().when('receipt', {
        is: 'email',
        then: (schema) => schema.email(t('flipAndTip.errors.emailInvalid')).required(t('flipAndTip.errors.emailRequired')),
        otherwise: (schema) => schema.notRequired()
      }),
      smsRegion: string().label(t('flipAndTip.labels.phoneCode')).when('receipt', {
        is: 'sms',
        then: (schema) => schema.required('Code is required'),
        otherwise: (schema) => schema.notRequired()
      }),
      smsPhone: string().label(t('flipAndTip.labels.phoneNumber')).when('receipt', {
        is: 'sms',
        then: (schema) => schema.min(10).required(),
        otherwise: (schema) => schema.notRequired()
      })
    }),
  });

  const { receipt, smsPhone } = form.state.watch();
  // formatted form fields can return a number value and/or a string during formatting
  // - convert back to a number or undefined
  const smsPhoneNumber = unknownToNumberOrUndefined(phoneNumber.us.unformat(smsPhone));
  
  const handleReceiptSend = async () => {
    if (typeof checkId !== 'number') return log.warn('handleReceiptSend()', '!checkId');
    if (typeof paymentId !== 'string') return log.warn('handleReceiptSend()', '!paymentId');
    
    const { isValid, values } = await form.action.submit();
    if (isValid) {
      paymentSendReceipt.mutate({
        checkId,
        paymentId,
        ...(values.receipt === 'email' ? { email: values.email } : undefined),
        ...(values.receipt === 'sms' ? {
          phoneNumber: `${globalPhoneCodes.getCodeFromRegionAbbr(values.smsRegion)}${values.smsPhone}`
        }: undefined)
      }, {
        onError: (err) => {
          toast.show.serviceError(err, {
            id: `${sectionId}.sendEmailReceiptError`,
            title: values.receipt === 'email'
              ? t('payment.error.failedToEmailReceipt')
              : t('payment.error.failedToSMSReceipt'),
          });
        },
        onSuccess: () => {
          toast.make({
            id: `${sectionId}.sendEmailReceiptSuccess`,
            type: 'custom',
            render: (model) => (
              <FlipAndTipReceiptToast
                message={t('flipAndTip.labels.receiptSuccessText')}
                title={t('flipAndTip.labels.receiptSentSuccessTitle')}
                doneLabel={t('flipAndTip.labels.receiptSuccessDone')}
                onDone={() => toast.store?.hide(model)}
              />
            )
          }).show();
        },
      });
    }
  };

  const handlePrintReceipt = async () => {
    printerSend.mutate({ type: 'Payment', checkId, paymentId }, {
      onError: (err) => toast.show.serviceError(err, { id: `${sectionId}.printReceiptError` }),
      onSuccess: () => {
        toast.make({
          id: `${sectionId}.printReceiptSuccess`,
          type: 'custom',
          render: (model) => <FlipAndTipReceiptToast message={t('flipAndTip.labels.receiptSuccessText')} title={t('flipAndTip.labels.printReceiptSuccessTitle')} doneLabel={t('flipAndTip.labels.receiptSuccessDone')} onDone={() => toast.store?.hide(model)} />
        }).show();
      },
    });
  };

  const handleNoReceipt = () => {
    toast.make({
      id: `${sectionId}.noReceiptMessage`,
      type: 'custom',
      render: (model) => <FlipAndTipReceiptToast title={t('flipAndTip.labels.noReceiptTitle')} message={t('flipAndTip.labels.receiptSuccessText')} doneLabel={t('flipAndTip.labels.receiptSuccessDone')} onDone={() => toast.store?.hide(model)} />
    }).show();
  };

  const handleSmsInputChange = (newValue?: string) => {
    const telHasReachedMax = !smsPhoneNumber || smsPhoneNumber.toString().length >= PHONE_INPUT_MAX_CHAR;
    const newValueExceedsMax = newValue && newValue.length >= 10;
    if (!telHasReachedMax || !newValueExceedsMax) {
      form.action.setValue('smsPhone', newValue ? parseInt(newValue): undefined, {
        shouldValidate: true,
      });
    }
  };

  return (
    <FormProvider usedForm={form}>
      <Page
        data-testid={TEST_ID.PAGE}
        mode="lite"
        className={styles.root}
        main={(
          <PageMain
            main={(
              <div className={styles.main}>
                <FlipAndTipLocationLogo />
                <Copy variant="h4" align="center" value={t('flipAndTip.labels.thankYouTitle')} />
                <Copy variant="body" weight="md" align="center" value={t('flipAndTip.labels.thankYouSubTitle')} />
                <Flex inline>
                  <FlipAndTipReceiptToggle
                    onChange={(value) => {
                      form.action.setValue('receipt', value);
                      if (value === 'noReceipt') {
                        handleNoReceipt();
                      } else if (value === 'print') {
                        handlePrintReceipt();
                      }
                    }}
                    selected={receipt}
                  />
                  <Field
                    className={styles.email_field}
                    type="email"
                    name="email"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        void handleReceiptSend();
                      }
                    }}
                    label={t('labels.email')}
                    hidden={receipt !== 'email'}
                    variant="underline"
                  />
                  <CTA
                    className={classNames(styles.submit_cta, {
                      [styles.visible]: receipt === 'email',
                      [styles.disabled]: form._rhf.getFieldState('email').invalid,
                    })}
                    pill
                    color="primary"
                    size="lg"
                    fullWidth
                    label={t('flipAndTip.labels.continue')}
                    onClick={handleReceiptSend}
                  />
                  <Flex horizontal
                    gap="none"
                    className={classNames({ [styles.hidden_field]: receipt !== 'sms' })}>
                    <Field
                      className={styles.sms_phone_code_field}
                      type="select"
                      name="smsRegion"
                      label={t('labels.phone')}
                      hidden={receipt !== 'sms'}
                      variant="no-background"
                      options={globalPhoneCodes.options}
                      renderValue={([singleSelectValue]) => globalPhoneCodes.getRenderedOption(singleSelectValue)}
                    />
                    <Field
                      className={styles.sms_phone_field}
                      type="tel"
                      name="smsPhone"
                      hidden={receipt !== 'sms'}
                      variant="underline"
                      align="left"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          void handleReceiptSend();
                        }
                      }}
                    />
                  </Flex>
                  <Flex
                    horizontal
                    justifyContent="center"
                    alignItems="center"
                    padding="md"
                    className={classNames({ [styles.hidden_field]: receipt !== 'sms' })}
                  >
                    <NumericKeyboard
                      value={smsPhoneNumber}
                      type="tel"
                      isValid={!form.action.getFieldState('smsPhone').invalid}
                      onSubmit={handleReceiptSend}
                      onChange={handleSmsInputChange}
                    />
                  </Flex>
                </Flex>
              </div>
            )}
          />
        )}
      />
    </FormProvider>
  );
};

// default export as this file is lazy loaded
export default FlipAndTipEndPage;
export { TEST_ID };
